type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  timestamp: number;
  component?: string;
}

class Logger {
  private static instance: Logger;
  private isDevelopment = import.meta.env.DEV;
  private minLevel: LogLevel = this.isDevelopment ? 'debug' : 'info';

  private levelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.levelPriority[level] >= this.levelPriority[this.minLevel];
  }

  private formatMessage(entry: LogEntry): string {
    const time = new Date(entry.timestamp).toISOString();
    const component = entry.component ? `[${entry.component}]` : '';
    return `${time} ${entry.level.toUpperCase()} ${component} ${entry.message}`;
  }

  private log(level: LogLevel, message: string, context?: Record<string, unknown>, component?: string): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      context,
      timestamp: Date.now(),
      component,
    };

    const formattedMessage = this.formatMessage(entry);
    const logFn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;

    if (context && Object.keys(context).length > 0) {
      logFn(formattedMessage, context);
    } else {
      logFn(formattedMessage);
    }

    // Could send to error tracking service here
    if (level === 'error') {
      // sendToErrorTracking(entry);
    }
  }

  debug(message: string, context?: Record<string, unknown>, component?: string): void {
    this.log('debug', message, context, component);
  }

  info(message: string, context?: Record<string, unknown>, component?: string): void {
    this.log('info', message, context, component);
  }

  warn(message: string, context?: Record<string, unknown>, component?: string): void {
    this.log('warn', message, context, component);
  }

  error(message: string, error?: Error, context?: Record<string, unknown>, component?: string): void {
    this.log('error', message, { ...context, error: error?.message, stack: error?.stack }, component);
  }

  // Create a child logger with preset component name
  child(component: string): ComponentLogger {
    return new ComponentLogger(this, component);
  }
}

class ComponentLogger {
  constructor(private parent: Logger, private component: string) {}

  debug(message: string, context?: Record<string, unknown>): void {
    this.parent.debug(message, context, this.component);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.parent.info(message, context, this.component);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.parent.warn(message, context, this.component);
  }

  error(message: string, error?: Error, context?: Record<string, unknown>): void {
    this.parent.error(message, error, context, this.component);
  }
}

export const logger = Logger.getInstance();
export type { LogLevel, LogEntry };
